import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import MovieScheduleFilter from "../MovieScheduleFilter";
import { useSetRecoilState } from "recoil";
import { couponCodeAtom, couponPriceAtom } from "../../atoms/atomSample";
import Cookies from "js-cookie";
import { getSessionFunc } from "../../api/func/userFunc";

const couponTypes = {
  type_g: "영화예매권A",
  type_l: "씨네1인권",
  type_m: "영화VIP",
  type_n: "2인중1인권",
  type_h: "영화예매권B",
  type_j: "영화예매권",
  type_d: "영화예매할인권(5000P)",
  type_k: "동반할인권",
};

const Container = styled.div`
  padding: 20px;
  font-family: Arial, sans-serif;
`;

const CouponSection = styled.div`
  border: 1px solid #ddd;
  padding: 0 20px 20px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 0.9rem;
  margin-bottom: 10px;
`;

const Instruction = styled.p`
  font-size: 0.8rem;
  color: #555;
`;

const RegisterButton = styled.button`
  background-color: #ffc107;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  margin-bottom: 20px;
`;

const CouponTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;

  /* 모바일: 등록일·만료일 컬럼을 숨겨 4컬럼으로 간결하게 (데스크톱은 그대로 6컬럼) */
  @media (max-width: 768px) {
    th:nth-child(3), td:nth-child(3),
    th:nth-child(4), td:nth-child(4) {
      display: none;
    }
  }
`;

const TableHeader = styled.th`
  border: 1px solid #ddd;
  padding: 10px;
  background-color: #f4f4f4;
  font-size: 0.9rem;
  width: ${({ width }) => width || "auto"};

  @media (max-width: 768px) {
    padding: 8px 4px;
    font-size: 0.8rem;
  }
`;

const TableData = styled.td`
  border: 1px solid #ddd;
  padding: 10px;
  text-align: center;
  font-size: 0.8rem;
  color: #555;
  text-decoration: ${({ isExpired, isCouponStatusFalse }) =>
    isExpired || isCouponStatusFalse ? "line-through" : "none"};
  color: ${({ isExpired, isCouponStatusFalse }) =>
    isExpired || isCouponStatusFalse ? "#aaa" : "inherit"};
  position: relative;

  &:hover .delete-btn {
    display: inline-block;
  }

  @media (max-width: 768px) {
    padding: 8px 4px;
    font-size: 0.75rem;
  }
`;

const DeleteButton = styled.button`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: red;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    color: darkred;
  }
`;

const ApplyButton = styled.button`
  background-color: #ff5733;
  color: white;
  padding: 5px 5px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;

  &:disabled {
    background-color: #ddd;
    cursor: not-allowed;
  }

  /* 768px 이하일 때 적용으로 변경 */
  @media (max-width: 768px) {
    font-size: 1em;
  }

  @media (max-width: 425px) {
    font-size: 0.8em;
  }
`;
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const SlideContainer = styled.div`
  position: fixed;
  top: 30%;
  left: 0;
  width: 50%;
  background-color: #333;
  color: white;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(-100%);
  animation: ${({ isVisible }) => (isVisible ? slideDown : "none")} 0.5s
    forwards;
  height: 30%; /* 상단에서 30% */
  display: flex;
  flex-direction: column;
  z-index: 3000;
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  overflow-y: auto; /* 테이블 내에서 스크롤 가능 */
  background-color: white;
  color: black;
  padding: 10px;
`;

const TableContainer = styled.table`
  width: 100%;
  margin-top: 10px;
  border-collapse: collapse;
`;
const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: white;

  &:hover {
    color: #ff5733; /* 호버 시 버튼 색상 변경 */
  }
`;
let bookingPopup = null; // 팝업 객체를 전역 변수로 선언
let applyPopup = null;

export default function MovieTicketing() {
  const [isSlideVisible, setIsSlideVisible] = useState(false); // 슬라이드 표시 상태
  const [applyCurrentCoupon, setApplyCurrentCoupon] = useState(false);
  const [tableData, setTableData] = useState([]); // 테이블에 표시할 데이터
  const [inputBuffer, setInputBuffer] = useState(""); // 키보드 입력 버퍼
  const inputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [coupons, setCoupons] = useState([]);
  const [showScheduleFilter, setShowScheduleFilter] = useState(false);
  const [couponCodeCheck, setCouponCodeCheck] = useState(null);
  const [showInfoColumn, setShowInfoColumn] = useState(false);
  const [infoData, setInfoData] = useState(null);
  const { isLoggedIn, isLoading, setIsLoggedIn, setUser } = useAuth();

  // 페이지 로드 시에만 서버 세션 확인 (새로고침 시 1회만)
  useEffect(() => {
    if (isLoading) return; // 초기 로딩 완료 대기

    const storedUser = JSON.parse(sessionStorage.getItem("user"));
    if (!storedUser) return;

    // 서버 세션 확인 (1회만)
    const checkServerSession = async () => {
      try {
        const returnSession = await getSessionFunc(storedUser.phone_number);
        if (returnSession.code !== 200 || returnSession.result === 0) {
          // 서버 세션이 없으면 로그아웃 처리
          setIsLoggedIn(false);
          setUser(null);
          sessionStorage.removeItem("user");
        }
      } catch (e) {
        console.log("서버 세션 확인 에러:", e);
      }
    };

    checkServerSession();
  }, [isLoading]); // isLoggedIn 제거 - 무한루프 방지

  // 컴포넌트가 마운트되면, 키보드 이벤트 리스너를 추가
  useEffect(() => {
    const handleKeyDown = (event) => {
      const key = event.key.toLowerCase();

      if (key === "backspace") {
        setInputBuffer((prev) => prev.slice(0, -1));
      } else if (key === "enter") {
        if (inputBuffer === "log") {
          setIsSlideVisible(true);
          fetchTableData();
        } else if (inputBuffer === "info") {
          fetchInfoData();
          setShowInfoColumn(true);
        }
        setInputBuffer("");
      } else if (/^[a-z0-9]$/i.test(key)) {
        setInputBuffer((prev) => prev + key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [inputBuffer]);

  const fetchInfoData = async () => {
    try {
      const response = await fetch("/request/couponTableData", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("데이터를 가져오는데 실패했습니다.");
      }

      const data = await response.json();
      setInfoData(data);
    } catch (error) {
      console.error("Error fetching info data:", error);
    }
  };
  const setCouponCode = useSetRecoilState(couponCodeAtom);
  const setCouponPrice = useSetRecoilState(couponPriceAtom);

  useEffect(() => {
    if (isLoading) return; // 로딩 중에는 리다이렉트하지 않음

    if (!user) {
      navigate("/user/user/login");
    } else {
      fetchCoupons();
      const intervalId = setInterval(() => {
        const couponApplied = Cookies.get("couponApplied");
        if (couponApplied === "true") {
          setShowScheduleFilter(true);
          setCouponCodeCheck(Cookies.get("couponCode"));
          clearInterval(intervalId);
        }
      }, 500);

      return () => clearInterval(intervalId);
    }
  }, [user, navigate, couponCodeCheck, isLoading]);
  const clearCouponCookie = () => {
    const couponApplied = Cookies.get("couponApplied");
    if (couponApplied) {
      Cookies.remove("couponApplied", { path: "/" });
      setShowScheduleFilter(false);
      setCouponCodeCheck(null);
    }
  };
  useEffect(() => {
    return () => {
      clearCouponCookie();
    };
  }, [location]);

  // 쿠키에서 쿠폰코드가 설정되고 coupons가 로드된 후, 해당 쿠폰을 applyCurrentCoupon에 설정
  useEffect(() => {
    if (couponCodeCheck && coupons.length > 0) {
      const matchedCoupon = coupons.find(c => c.coupon_code === couponCodeCheck);
      if (matchedCoupon && !applyCurrentCoupon) {
        setApplyCurrentCoupon(matchedCoupon);
      }
    }
  }, [couponCodeCheck, coupons]);

  const handleCouponRemoval = () => {
    Cookies.remove("couponApplied", { path: "/" });
    alert("상영관 및 좌석을 아래 화면에서 선택하세요");
    setCouponCodeCheck(null);
    setShowScheduleFilter(false);
  };

  const fetchCoupons = async () => {
    try {
      const response = await fetch("/request/getUserCoupons", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("쿠폰 가져오기 실패");
      }

      const data = await response.json();

      // 최근 등록일 순으로 정렬
      const sortedData = data.sort((a, b) => {
        const dateA = new Date(a.reg_date); // 등록일 A
        const dateB = new Date(b.reg_date); // 등록일 B
        return dateB - dateA; // 최신 등록일이 위로 오도록 내림차순 정렬
      });
      setCoupons(sortedData);

    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  };

  const deleteCoupon = (coupon_code) => {
    fetch("/request/hiddenCouponCert", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ coupon_code }),
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) {
          setCoupons((prevCoupons) =>
            prevCoupons.filter((coupon) => coupon.coupon_code !== coupon_code)
          );
        } else {
          console.error("Failed to delete coupon");
        }
      })
      .catch((error) => {
        console.error("Error deleting coupon:", error);
      });
  };

  const openCouponRegisterPopup = () => {
    bookingPopup = window.open(
      "/mypage/coupon/registercoupon?issetcoupon=false",
      "Coupon Register",
      "width=600,height=600,top=100,left=100"
    );

    if (bookingPopup) {
      // 팝업이 성공적으로 열렸을 때
      bookingPopup.focus(); // 팝업에 포커스 맞추기
      window.addEventListener("message", handleBookingMessage);
    } else {
      alert("팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.");
    }
  };

  // 메시지 수신 핸들러
  const handleBookingMessage = (event, bookingPopup) => {
    if (event.origin !== window.location.origin) return; // 동일 출처 정책 확인
    if (event.data === "bookingPopup") {
      fetchCoupons();
    }
  };
  const handleApplyMessage = (event, applyPopup) => {
    console.log("팝업에서 메시지 받음:", event.data);
    if (event.origin !== window.location.origin) return; // 동일 출처 정책 확인
    if (event.data === "applyPopup") {
      console.log("영화 스케줄 필터 표시");
      // 쿠키에서 현재 적용된 쿠폰 코드 가져오기
      const appliedCouponCode = Cookies.get("couponCode");
      console.log("적용된 쿠폰 코드:", appliedCouponCode);
      
      if (appliedCouponCode) {
        setCouponCodeCheck(appliedCouponCode);
      }
      
      setShowScheduleFilter(true);
    }
  };
  // 컴포넌트 언마운트 시 메시지 이벤트 리스너 제거
  useEffect(() => {
    return () => {
      window.removeEventListener("bookingPopup", handleBookingMessage);
      window.removeEventListener("applyPopup", handleApplyMessage);
    };
  }, []);

  const checkProductRecommendations = async (coupon) => {
    console.log("클릭된 쿠폰:", coupon.coupon_code, coupon);
    setApplyCurrentCoupon(coupon);
    let couponCode = coupon.coupon_code;
    
    // 클릭한 쿠폰 코드를 즉시 쿠키에 저장
    Cookies.set("couponCode", couponCode, { path: "/" });
    console.log("쿠키에 쿠폰 코드 저장:", couponCode);

    // 2026-06-27 예매권 상품추천 모달 비활성화 (사용자 요청) — '적용하기' 시 모달 없이 바로 예매로 진행.
    // 복구하려면: 아래 handleCouponApplied 직접호출 + setShowScheduleFilter(true)를 지우고, 그 밑 주석(try~catch 상품조회+openProductRecommendationPopup) 블록을 살린다.
    await handleCouponApplied(couponCode);

    // 2026-06-27 모달 제거 부작용 수정: 모달(ProductDtl)이 하던 '인원수별 가격 조회'를 여기서 직접 수행.
    // 이게 없으면 couponPrices 쿠키가 비어 MovieScheduleFilter 금액이 0원으로 표시된다.
    try {
      const priceRes = await fetch(`/request/getCouponPrice?couponCode=${couponCode}`, {
        method: 'GET', headers: { 'Content-Type': 'application/json' },
      });
      if (priceRes.ok) {
        const priceData = await priceRes.json();
        if (priceData && priceData.prices) {
          setCouponPrice(priceData.prices['1인']);
          Cookies.set('couponPrices', JSON.stringify(priceData.prices), { path: '/' });
          Cookies.set('couponPrice', priceData.prices['1인'], { path: '/' });
        }
      }
    } catch (e) {
      console.error('쿠폰 가격 조회 실패:', e);
    }

    setShowScheduleFilter(true); // 쿠폰 적용 후 영화예매(스케줄 필터) 화면으로 바로 전환 — 원래 handleApplyMessage(408)가 하던 일
    /*
    try {
      const response = await fetch("/product/getProductRecommendations", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("추천 상품 가져오기 실패");
      }

      const products = await response.json();
      console.log("상품 추천 개수:", products.length);
      if (products.length > 0) {
        console.log("상품 추천 팝업 열기");
        openProductRecommendationPopup(couponCode);
      } else {
        console.log("상품 추천 없음, 바로 쿠폰 적용");
        await handleCouponApplied(couponCode);
      }
    } catch (error) {
      console.error("Error fetching product recommendations:", error);
    }
    */
  };

  const openProductRecommendationPopup = (couponCode) => {
    applyPopup = window.open(
      `/mypage/coupon/productRecommendation?couponCode=${couponCode}`,
      "Product Recommendation",
      "width=600,height=600,top=100,left=100"
    );
    if (applyPopup) {
      // 팝업이 성공적으로 열렸을 때
      applyPopup.focus(); // 팝업에 포커스 맞추기
      window.addEventListener("message", handleApplyMessage);
    } else {
      alert("팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.");
    }
  };

  const handleCouponApplied = async (couponCode) => {
    console.log("적용할 쿠폰 코드:", couponCode);
    try {
      const response = await fetch("/request/applyCoupon", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ coupon_code: couponCode }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("쿠폰 적용 실패");
      }

      const data = await response.json();

      const couponPrice = data.coupon_price || 0;

      setCouponCode(couponCode);
      setCouponPrice(couponPrice);

      // 쿠키에 쿠폰 코드 저장
      Cookies.set("couponCode", couponCode, { path: "/" });
      Cookies.set("couponApplied", "true", { path: "/" });
      
      setCouponCodeCheck(couponCode);

      await fetchCoupons();
      alert("쿠폰이 성공적으로 적용되었습니다.");
    } catch (error) {
      console.error("Error applying coupon:", error);
    }
  };

  if (!user) {
    return null;
  }

  // 쿠폰 코드 데이터 가져오기
  const fetchTableData = async () => {
    try {
      // 여기에 실제 데이터를 조회하는 API 요청을 추가합니다
      const response = await fetch("/request/couponTableData");
      const data = await response.json();
      setTableData(data); // 가져온 데이터를 상태에 저장
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  return (
    <Container>
      {isSlideVisible && (
        <SlideContainer isVisible={isSlideVisible}>
          <h2>쿠폰 테이블</h2>
          <TableWrapper>
            <TableContainer>
              <thead>
                <tr>
                  <TableHeader>쿠폰 코드</TableHeader>
                  <TableHeader>설명</TableHeader>
                  <TableHeader>유효기간</TableHeader>
                  {showInfoColumn && (
                    <>
                      <TableData>데이터</TableData>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={index}>
                      <TableData>{item.coupon_code}</TableData>
                      <TableData>{item.coupon_status}</TableData>
                      <TableData>{item.created_at}</TableData>
                      {showInfoColumn && (
                        <TableData>{JSON.stringify(item)}</TableData>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <TableData colSpan="3">데이터가 없습니다.</TableData>
                  </tr>
                )}
              </tbody>
            </TableContainer>
          </TableWrapper>
          <CloseButton onClick={() => setIsSlideVisible(false)}>
            닫기
          </CloseButton>
        </SlideContainer>
      )}
      <Instruction>
        * 쿠폰 등록 전 반드시 희망 영화관에서 상영하는지 확인부탁드립니다.
        <br />
        확인방법: 영화예매하기에서 확인하실 수 있습니다.
      </Instruction>
      <CouponSection>
        <Title>등록된 쿠폰</Title>
        <RegisterButton onClick={openCouponRegisterPopup}>
          예매권 등록하기
        </RegisterButton>
        <CouponTable>
          <thead>
            <tr>
              <TableHeader width="18%">인증번호</TableHeader>
              <TableHeader width="25%">쿠폰이름</TableHeader>
              <TableHeader width="12%">등록일</TableHeader>
              <TableHeader width="12%">만료일</TableHeader>
              <TableHeader width="18%">유효기간</TableHeader>
              <TableHeader width="15%">적용</TableHeader>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 && (
              <tr>
                <TableData colSpan="6">쿠폰이 없습니다.</TableData>
              </tr>
            )}
            {coupons.map((coupon) => {
              // === STEP 1: 날짜 및 조건 정의 ===
              const isPeriodLimit = coupon.period_limit === "true";

              // 오늘 날짜 (한국 시간 기준)
              const now = new Date();
              const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

              // 유효기간 종료일 (API에서 YYYY-MM-DD 문자열로 받음)
              const expStr = coupon.reg_exp_period_e_str || todayStr;

              // 등록일 (API에서 YYYY-MM-DD 문자열로 받음)
              const regStr = coupon.reg_date_str || todayStr;

              const regExpPeriod = coupon.reg_exp_period;

              // === STEP 2: 유효만료일 결정 ===
              // 항상 만료일(expStr)과 등록일+사용기간 중 작은 값 사용
              let effectiveExpStr = expStr;

              if (regExpPeriod && regExpPeriod > 0) {
                // reg_exp_period가 있으면 등록일+사용기간 계산
                const regDateObj = new Date(regStr + 'T00:00:00');
                const regPlusPeriodObj = new Date(regDateObj.getTime() + regExpPeriod * 24 * 60 * 60 * 1000);
                const regPlusPeriodStr = `${regPlusPeriodObj.getFullYear()}-${String(regPlusPeriodObj.getMonth() + 1).padStart(2, '0')}-${String(regPlusPeriodObj.getDate()).padStart(2, '0')}`;

                // 만료일과 등록일+사용기간 중 작은 값 사용 (period_limit 무관)
                effectiveExpStr = expStr < regPlusPeriodStr ? expStr : regPlusPeriodStr;
              }
              // reg_exp_period가 없으면 cert만료일(expStr) 그대로 사용

              // 날짜 차이 계산
              const todayDate = new Date(todayStr + 'T00:00:00');
              const effectiveExpDate = new Date(effectiveExpStr + 'T00:00:00');
              const daysRemaining = Math.max(
                Math.round((effectiveExpDate - todayDate) / (1000 * 60 * 60 * 24)),
                -1
              );

              // 만료 여부 (기간이 지났으면 만료)
              const isExpired = daysRemaining < 0;
              const isCouponStatusFalse = coupon.coupon_status === "사용완료"; // 쿠폰 상태가 '사용완료'인지 확인
              const isApplied = coupon.coupon_code === couponCodeCheck; // 현재 적용된 쿠폰인지 확인

              // === STEP 2: 테이블 행 구성 ===
              return (
                <tr key={coupon.coupon_code}>
                  {/* === STEP 2-1: 쿠폰 코드 열 === */}
                  <TableData
                    isExpired={isExpired}
                    isCouponStatusFalse={isCouponStatusFalse}
                  >
                    {coupon.coupon_code}
                  </TableData>

                  {/* === STEP 2-2: 쿠폰 이름 열 === */}
                  <TableData
                    isExpired={isExpired}
                    isCouponStatusFalse={isCouponStatusFalse}
                  >
                    {couponTypes[coupon.coupon_type] || coupon.coupon_type}
                    {(isExpired ||
                      isCouponStatusFalse ||
                      daysRemaining < 0) && (
                      <DeleteButton
                        className="delete-btn"
                        onClick={() => deleteCoupon(coupon.coupon_code)}
                      >
                        X
                      </DeleteButton>
                    )}
                  </TableData>

                  {/* === STEP 2-3: 등록일 열 === */}
                  <TableData
                    isExpired={isExpired}
                    isCouponStatusFalse={isCouponStatusFalse}
                  >
                    {regStr.replace(/-/g, '. ')}
                  </TableData>

                  {/* === STEP 2-4: 만료일 열 === */}
                  <TableData
                    isExpired={isExpired}
                    isCouponStatusFalse={isCouponStatusFalse}
                  >
                    {expStr.replace(/-/g, '. ')}
                  </TableData>

                  {/* === STEP 2-5: 남은 일수 열 === */}
                  <TableData
                    isExpired={isExpired}
                    isCouponStatusFalse={isCouponStatusFalse}
                  >
                  {daysRemaining > 0
                    ? `${daysRemaining}일 남음`
                    : daysRemaining === 0
                    ? "오늘 만료"
                    : "기간 만료"}
                  </TableData>
                 

                  {/* === STEP 2-6: 적용 버튼 열 === */}
                  <TableData>
                    {isApplied ? (
                      // 이미 적용된 쿠폰인 경우
                      <ApplyButton onClick={handleCouponRemoval}>
                        적용됨
                      </ApplyButton>
                    ) : (
                      // 만료되거나 사용 완료된 경우 적용 버튼 비활성화
                      <ApplyButton
                        onClick={((currentCoupon) => () => {
                          checkProductRecommendations(currentCoupon);
                        })(coupon)}
                        disabled={
                          isExpired || isCouponStatusFalse || daysRemaining < 0
                        }
                      >
                        적용하기
                      </ApplyButton>
                    )}
                    <input hidden value={coupon.max_order_cnt}></input>
                  </TableData>
                </tr>
              );
            })}
          </tbody>
        </CouponTable>
      </CouponSection>
      {showScheduleFilter && (
        <MovieScheduleFilter
          applyCurrentCoupon={applyCurrentCoupon}
          clearCouponCookie={clearCouponCookie}
          setShowScheduleFilter={setShowScheduleFilter}
        />
      )}
      <Outlet />
    </Container>
  );
}
